import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Play, CheckCircle2, XCircle, Clock, Wifi, WifiOff, Link2,
  CreditCard, Banknote, MessageSquare, RefreshCw, ChevronDown, ChevronRight,
  Zap, ArrowUpRight, History, Eye, Search, Globe,
} from "lucide-react";

const GROUP_ICONS = {
  pg: CreditCard, bbps: Banknote, payout: ArrowUpRight, messaging: MessageSquare,
};

const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    });
  } catch { return "—"; }
};

export default function AdminSystemTest() {
  const [providers, setProviders] = useState([]);
  const [relayConfigured, setRelayConfigured] = useState(false);
  const [relayUrl, setRelayUrl] = useState("");
  const [isPreview, setIsPreview] = useState(true);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runningKey, setRunningKey] = useState(null);
  const [liveResults, setLiveResults] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [detailRun, setDetailRun] = useState(null);
  const [expandedProviders, setExpandedProviders] = useState({});
  const [verifyTxnId, setVerifyTxnId] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const loadProviders = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/system-test/providers");
      setProviders(data.providers || []);
      setRelayConfigured(data.relay_configured || false);
      setRelayUrl(data.relay_url || "");
      setIsPreview(data.is_preview !== false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const { data } = await api.get("/admin/system-test/runs?limit=10");
      setRuns(data.runs || []);
    } catch (err) { /* ignore */ }
    finally { setRunsLoading(false); }
  }, []);

  useEffect(() => { loadProviders(); loadRuns(); }, [loadProviders, loadRuns]);

  const runTests = async (providerKeys) => {
    const isAll = !providerKeys;
    if (isAll) setRunning(true);
    else setRunningKey(providerKeys[0]);
    setLiveResults(null);

    try {
      const body = providerKeys ? { providers: providerKeys } : {};
      const { data } = await api.post("/admin/system-test/run", body);
      setLiveResults(data);
      // Auto-expand all providers
      const expanded = {};
      Object.keys(data.results || {}).forEach(k => { expanded[k] = true; });
      setExpandedProviders(expanded);

      if (data.total_failed === 0) {
        toast.success(`All ${data.total_tests} tests passed via live relay`);
      } else {
        toast.warning(`${data.total_passed} passed, ${data.total_failed} failed`);
      }
      loadRuns();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Test run failed");
    } finally {
      setRunning(false);
      setRunningKey(null);
    }
  };

  const verifyTxn = async () => {
    if (!verifyTxnId.trim()) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await api.post("/admin/system-test/verify-txn", { provider_order_id: verifyTxnId.trim() });
      setVerifyResult(data);
      if (data.ok) toast.success("Transaction verified via live relay");
      else toast.error(data.error || "Verification failed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setVerifying(false); }
  };

  const toggleExpand = (key) => setExpandedProviders(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-system-test">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="overline">ADMIN</span>
            <h1 className="font-display text-2xl sm:text-3xl font-bold mt-2">Testing System Connection</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Live gateway connectivity tests. All API calls are proxied through the live system
              to bypass preview IP restrictions.
            </p>
          </div>
          <Button
            onClick={() => runTests(null)}
            disabled={running || (isPreview && !relayConfigured)}
            size="lg"
            className="gap-2 bg-emerald-600 hover:bg-emerald-700"
            data-testid="run-all-tests-btn"
          >
            {running ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5" />}
            Run All Tests
          </Button>
        </div>

        {/* Relay Status Banner — only on preview */}
        {isPreview && (
          <div className={`surface-card p-4 border-l-4 ${relayConfigured ? "border-l-emerald-500" : "border-l-red-500"}`} data-testid="relay-status">
            <div className="flex items-center gap-3">
              {relayConfigured ? <Globe className="h-5 w-5 text-emerald-600" /> : <WifiOff className="h-5 w-5 text-red-600" />}
              <div>
                <div className={`font-semibold text-sm ${relayConfigured ? "text-emerald-800" : "text-red-800"}`}>
                  {relayConfigured ? "Live Relay Connected" : "Live Relay Not Connected"}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {relayConfigured
                    ? <span>Preview proxies gateway calls through <span className="font-mono font-semibold">{relayUrl}</span></span>
                    : <span>Set <span className="font-mono">LIVE_RELAY_URL</span> and <span className="font-mono">RELAY_SECRET</span> in backend .env, then deploy</span>}
                </div>
              </div>
            </div>
          </div>
        )}

        {!isPreview && (
          <div className="surface-card p-4 border-l-4 border-l-emerald-500" data-testid="live-status">
            <div className="flex items-center gap-3">
              <Globe className="h-5 w-5 text-emerald-600" />
              <div>
                <div className="font-semibold text-sm text-emerald-800">Live Production System</div>
                <div className="text-xs text-muted-foreground mt-0.5">Tests will execute directly against real gateways from this server.</div>
              </div>
            </div>
          </div>
        )}

        {/* Live Results Summary */}
        {liveResults && (
          <div className="surface-card p-5 border-l-4 border-l-blue-500" data-testid="live-results-summary">
            <div className="flex items-center gap-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Latest Run via Live Relay</div>
                <div className="text-lg font-bold mt-1">
                  {liveResults.total_failed === 0
                    ? <span className="text-emerald-700">All {liveResults.total_tests} tests passed</span>
                    : <span className="text-amber-700">{liveResults.total_passed} passed, {liveResults.total_failed} failed</span>}
                </div>
              </div>
              <div className="text-xs text-muted-foreground font-mono">{liveResults.total_duration_ms}ms total</div>
            </div>
          </div>
        )}

        {/* Provider Cards */}
        <div className="space-y-3">
          {loading ? (
            <div className="p-12 text-center text-muted-foreground"><Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" />Loading...</div>
          ) : providers.map((p) => {
            const GroupIcon = GROUP_ICONS[p.group] || Zap;
            const results = liveResults?.results?.[p.key] || [];
            const passed = results.filter(r => r.passed).length;
            const failed = results.filter(r => !r.passed).length;
            const expanded = expandedProviders[p.key];

            return (
              <div key={p.key} className="surface-card overflow-hidden" data-testid={`provider-card-${p.key}`}>
                <div className="p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => results.length > 0 && toggleExpand(p.key)}>
                    <div className={`p-2 rounded-lg ${relayConfigured ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"}`}>
                      <GroupIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm flex items-center gap-2">
                        {p.label}
                        <Badge variant="outline" className={`text-[10px] ${
                          !isPreview ? "text-emerald-700 border-emerald-300 bg-emerald-50" :
                          relayConfigured ? "text-blue-700 border-blue-300 bg-blue-50" :
                          "text-gray-500 border-gray-300 bg-gray-50"
                        }`}>
                          {!isPreview ? <><Wifi className="h-2.5 w-2.5 mr-1" />Direct</> :
                           relayConfigured ? <><Link2 className="h-2.5 w-2.5 mr-1" />Via live relay</> :
                           <><WifiOff className="h-2.5 w-2.5 mr-1" />Not connected</>}
                        </Badge>
                      </div>
                    </div>
                    {results.length > 0 && (
                      <div className="flex items-center gap-2 mr-3">
                        <Badge variant="outline" className={`text-xs ${failed > 0 ? "text-red-700 border-red-300 bg-red-50" : "text-emerald-700 border-emerald-300 bg-emerald-50"}`}>
                          {failed > 0 ? <XCircle className="h-3 w-3 mr-1" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                          {passed}/{results.length} passed
                        </Badge>
                        {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm" variant="outline"
                    disabled={running || runningKey === p.key || !relayConfigured}
                    onClick={(e) => { e.stopPropagation(); runTests([p.key]); }}
                    className="gap-1.5"
                    data-testid={`run-test-${p.key}`}
                  >
                    {runningKey === p.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                    Test
                  </Button>
                </div>
                {expanded && results.length > 0 && (
                  <div className="border-t border-border bg-accent/30 divide-y divide-border">
                    {results.map((r, i) => (
                      <div key={i} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                        {r.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" /> : <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-xs">{r.name}</div>
                          <div className="text-xs text-muted-foreground truncate mt-0.5">{r.detail}</div>
                        </div>
                        <div className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">{r.duration_ms}ms</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Verify Transaction via Relay */}
        <div className="surface-card p-5 space-y-3" data-testid="verify-txn-section">
          <h2 className="font-display text-base font-bold flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" /> Verify Transaction via Live
          </h2>
          <p className="text-xs text-muted-foreground">Enter a ManaRecharge txnid/provider_order_id to verify its status through the live system.</p>
          <div className="flex gap-2 max-w-lg">
            <Input
              value={verifyTxnId} onChange={(e) => setVerifyTxnId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && verifyTxn()}
              placeholder="txnid or provider_order_id"
              data-testid="verify-txn-input"
            />
            <Button onClick={verifyTxn} disabled={verifying || !relayConfigured || !verifyTxnId.trim()} className="gap-1.5" data-testid="verify-txn-btn">
              {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Verify
            </Button>
          </div>
          {verifyResult && (
            <pre className="bg-muted rounded-md p-3 text-[11px] max-h-48 overflow-auto whitespace-pre-wrap">
              {JSON.stringify(verifyResult, null, 2)}
            </pre>
          )}
        </div>

        {/* Test Run History */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-bold flex items-center gap-2">
              <History className="h-5 w-5 text-muted-foreground" /> Test Run History
            </h2>
            <Button variant="outline" size="sm" onClick={loadRuns} disabled={runsLoading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${runsLoading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
          <div className="surface-card overflow-x-auto">
            {runs.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No test runs yet. Click "Run All Tests" to start.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-accent/50 text-[11px] uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">When</th>
                    <th className="px-3 py-2 text-left">Triggered by</th>
                    <th className="px-3 py-2 text-center">Passed</th>
                    <th className="px-3 py-2 text-center">Failed</th>
                    <th className="px-3 py-2 text-right">Duration</th>
                    <th className="px-3 py-2 text-left">Providers</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {runs.map((r) => (
                    <tr key={r.id} className="hover:bg-accent/30">
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtIST(r.started_at)}</td>
                      <td className="px-3 py-2 text-xs">{r.triggered_by_email || r.triggered_by}</td>
                      <td className="px-3 py-2 text-center"><span className="text-emerald-700 font-semibold">{r.total_passed}</span></td>
                      <td className="px-3 py-2 text-center"><span className={`font-semibold ${r.total_failed > 0 ? "text-red-700" : "text-muted-foreground"}`}>{r.total_failed}</span></td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{r.total_duration_ms}ms</td>
                      <td className="px-3 py-2 text-xs">
                        <div className="flex gap-1 flex-wrap">
                          {(r.providers_tested || []).slice(0, 4).map(p => (
                            <span key={p} className="bg-accent rounded px-1.5 py-0.5 text-[10px] font-mono">{p.replace("manarecharge_", "mr_")}</span>
                          ))}
                          {(r.providers_tested || []).length > 4 && <span className="text-[10px] text-muted-foreground">+{r.providers_tested.length - 4}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Button variant="ghost" size="sm" onClick={() => setDetailRun(r)}><Eye className="h-3.5 w-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Run Detail Dialog */}
      <Dialog open={!!detailRun} onOpenChange={() => setDetailRun(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="h-5 w-5" /> Test Run — {fmtIST(detailRun?.started_at)}</DialogTitle>
          </DialogHeader>
          {detailRun && (
            <div className="space-y-4">
              <div className="flex gap-6 text-sm flex-wrap">
                <div><span className="text-muted-foreground">By:</span> {detailRun.triggered_by_email}</div>
                <div><span className="text-muted-foreground">Duration:</span> {detailRun.total_duration_ms}ms</div>
                <div><span className="text-muted-foreground">Relay:</span> <span className="font-mono">{detailRun.relay_url}</span></div>
                <div className="text-emerald-700 font-semibold">{detailRun.total_passed} passed</div>
                {detailRun.total_failed > 0 && <div className="text-red-700 font-semibold">{detailRun.total_failed} failed</div>}
              </div>
              {Object.entries(detailRun.results || {}).map(([provKey, tests]) => {
                const pDef = providers.find(p => p.key === provKey) || { label: provKey };
                const passedCount = tests.filter(t => t.passed).length;
                return (
                  <div key={provKey} className="surface-card overflow-hidden">
                    <div className="p-3 bg-accent/40 flex items-center justify-between">
                      <div className="font-semibold text-sm">{pDef.label}</div>
                      <Badge variant="outline" className={`text-xs ${passedCount === tests.length ? "text-emerald-700 border-emerald-300" : "text-red-700 border-red-300"}`}>
                        {passedCount}/{tests.length} passed
                      </Badge>
                    </div>
                    <div className="divide-y divide-border">
                      {tests.map((t, i) => (
                        <div key={i} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                          {t.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" /> : <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs">{t.name}</div>
                            <div className="text-xs text-muted-foreground mt-0.5 break-all">{t.detail}</div>
                          </div>
                          <div className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">{t.duration_ms}ms</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
